// @flow

import styled from '@emotion/styled'
import * as React from 'react'

import { colors, fonts, media } from '@toggl/style'

import * as Responsive from '../responsive'
import { defaultText, paragraph2, paragraph3, paragraph4 } from '../text'

type Props = {
  children?: React.Node,
  logo: React.Element<any>,
  products: Array<React.Element<any>>,
  Link: any,
}

export function Wrapper({ children, logo, products, Link }: Props) {
  const hasChildren = !!children
  return (
    <Root>
      <DesktopWrapper hasChildren={hasChildren}>
        <LogoAndList>
          <Responsive.DesktopOnly>
            <LogoWrapper>{logo}</LogoWrapper>
          </Responsive.DesktopOnly>

          <Lists>
            <Responsive.MobileOnly>
              <LogoWrapper>{logo}</LogoWrapper>
            </Responsive.MobileOnly>
            <List title="Toggl Global">
              <li>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://toggl.com/blog/"
                >
                  Blog
                </a>
              </li>
              <li>
                <Link to="/mission/">Our Mission</Link>
              </li>
              <li>
                <Link to="/jobs/">Working at Toggl</Link>
              </li>
              <li>
                <Link to="/legal/">Legal Terms</Link>
              </li>
            </List>
            {children}
          </Lists>
        </LogoAndList>
        <RootProducts hasChildren={hasChildren}>
          <ProductsTitle hasChildren={hasChildren}>
            {hasChildren
              ? 'Discover other Toggl tools:'
              : 'Discover all three Toggl tools:'}
          </ProductsTitle>
          <Logoes hasChildren={hasChildren}>
            {hasChildren
              ? React.Children.map(products, (item) => <Logo>{item}</Logo>)
              : products}
          </Logoes>
        </RootProducts>
      </DesktopWrapper>
      <Bottom>
        <span>
          &#169; {new Date().getFullYear()} Toggl. All rights reserved.
        </span>
      </Bottom>
    </Root>
  )
}

export function List({
  title,
  children,
}: {
  title: string,
  children: React.Node,
}) {
  return (
    <RootList>
      <Title>{title}</Title>
      <Ul>{children}</Ul>
    </RootList>
  )
}

const Logo = styled.div`
  display: flex;
  flex-direction: column;
`

const LogoAndList = styled.div`
  ${media.mq[1]} {
    padding: 40px 40px 20px 40px;
  }
  ${media.mq[2]} {
    padding: 60px 60px 20px 60px;
  }
`

const ProductsTitle = styled.p`
  ${paragraph4};

  ${media.mq[1]} {
    width: 100%;
    ${paragraph3};
    margin-right: 80px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
  }
  ${media.mq[2]} {
    max-width: 150px;
    margin-bottom: 0;
  }
`

const DesktopWrapper = styled.div`
  ${media.mq[1]} {
    display: flex;
    background: ${colors.darkPurple};
    flex-direction: column;
  }
`

const Root: React.ComponentType<any> = styled.div`
  color: ${colors.almostWhite};
  z-index: 100;
  position: relative;

  svg {
    height: 28px;
    display: block;
    margin: 5px 0;
  }

  svg + span {
    display: block;
  }
`

const RootProducts: React.ComponentType<any> = styled.div`
  padding: 40px 50px;
  background: ${(p) =>
    p.hasChildren ? colors.fadedPurple : colors.darkPurple};
  ${ProductsTitle} {
    opacity: 1;
  }
  span {
    ${paragraph4};
    margin-bottom: 5px;
  }

  ${media.mq[1]} {
    display: flex;
    flex-wrap: wrap;
    background-color: ${colors.fadedPurple};
    span {
      line-height: 1.1;
      display: inline-block;
    }
  }
  ${media.mq[2]} {
    padding: 0 65px;
    flex-wrap: nowrap;
    height: 110px;
  }
`

const Logoes = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 25px;

  img + span {
    margin-top: 5px;
  }
  > *:not(:last-of-type) {
    margin-bottom: 30px;

    ${media.mq[1]} {
      margin-bottom: 0;
      margin-right: 80px;
    }
  }
  ${media.mq[1]} {
    margin-top: 0;
    flex-direction: row;
    align-items: center;
  }
`

const Title = styled.span`
  font-family: ${fonts.gtHaptikMedium};
  font-weight: ${fonts.bolder};
  line-height: 2.5;
  display: block;
  text-transform: uppercase;
  font-size: 0.9rem;
  margin: 10px 0;
  letter-spacing: 0.5px;
  opacity: 0.5;
  ${media.mq[1]} {
    line-height: 1.7;
    margin-bottom: 15px;
    opacity: 0.7;
  }
`
const RootList: React.ComponentType<any> = styled.div`
  padding-right: 35px;
  padding-left: 10px;
  min-width: 15rem;
  flex: 1;
  ${media.mq[1]} {
    flex: 0 1 calc(100% / 3);
  }
  ${media.mq[2]} {
    flex: 1;
  }
  &:last-of-type {
    margin-right: 0;
  }
`

const Bottom = styled.div`
  background: ${(p) => p.theme.primary};
  height: 70px;
  padding: 40px;
  ${paragraph4};
  align-items: center;
  display: flex;
  color: ${colors.darkPurple};

  ${media.mq[1]} {
    padding: 20px 50px;
    height: 52px;
  }
`
const Ul = styled.ul`
  ${paragraph3};
  list-style: none;
  padding: 0;
  margin: 0 0 35px 0;

  li {
    line-height: 2.5;
  }

  ${media.mq[1]} {
    ${paragraph2};
    li {
      line-height: 1.7;
    }
  }
`
const LogoWrapper = styled.div`
  margin-bottom: 35px;
  min-width: 100%;
`
const Lists: React.ComponentType<any> = styled.div`
  ${defaultText};
  position: relative;
  padding: 40px 40px 0;
  color: ${colors.almostWhite};
  background: ${colors.darkPurple};

  a {
    text-decoration: none;
    transition: color 300ms linear;
    color: ${colors.almostWhite};
    cursor: pointer;

    &:hover {
      color: ${(p) => p.theme.primary};
    }
  }

  ${media.mq[1]} {
    padding: 0;
    display: flex;
    flex-wrap: wrap;
  }
  ${media.mq[2]} {
    flex-wrap: no-wrap;
  }
`
